import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { FieldInput } from "@/components/member/FieldInput";
import { api } from "@/lib/api";

export { TITLES } from "@/components/member/FieldInput";

export const EMPTY_PROFILE = { title: "MLS", first_name: "", middle_name: "", surname: "", certificate_name: "", sex: "", facility: "", job_title: "", city: "", region: "Greater Accra", phone: "", alt_phone: "", dob: "", emergency_name: "", emergency_email: "", emergency_phone: "", emergency_relationship: "", custom: {} };

export const fromMember = (m) => ({ ...EMPTY_PROFILE, ...Object.fromEntries(Object.keys(EMPTY_PROFILE).filter((k) => m[k]).map((k) => [k, m[k]])) });

export const useMemberForm = () => {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/forms/member").then((r) => setForm(r.data)).catch(() => setForm(false)); }, []);
  return form;
};

const SLOTS = ["pin", "email", "gamls_id", "staff_id"];

const normPhone = (v) => {
  if (!/^\+?\d[\d\s-]*\d$/.test(v)) return null;
  const d = v.replace(/\D/g, "");
  if (/^0\d{9}$/.test(d)) return d;
  if (/^233\d{9}$/.test(d)) return `0${d.slice(3)}`;
  return v.startsWith("+") && d.length >= 8 && d.length <= 15 ? d : null;
};

const empty = (v) => (Array.isArray(v) ? !v.length : typeof v === "boolean" ? !v : !String(v ?? "").trim());

const fieldError = (f, v) => {
  const s = String(v ?? "").trim();
  if (f.required && empty(v)) return f.type === "checkbox" ? `Please tick '${f.label}'` : `${f.label} is required`;
  if (!s || f.type === "checkbox" || f.type === "multi") return null;
  if (f.type === "phone" && !normPhone(s)) return f.key === "phone" ? "Enter ONE valid contact number, e.g. 024 000 0000 or +233 24 000 0000" : `${f.label} is not a valid phone number`;
  if (f.type === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s)) return `${f.label} is not a valid email`;
  if (f.type === "number" && !/^-?\d+(\.\d+)?$/.test(s)) return `${f.label} must be a number`;
  if (f.key === "dob") {
    const age = (Date.now() - new Date(s).getTime()) / (365.25 * 864e5);
    if (!(age >= 16 && age <= 100)) return "Enter a valid date of birth";
  }
  return null;
};

export const profileError = (p, form) => {
  for (const f of form.sections.flatMap((s) => s.fields)) {
    if (SLOTS.includes(f.key) || (f.builtin && p[f.key] === undefined)) continue;
    const err = fieldError(f, f.builtin ? p[f.key] : p.custom?.[f.key]);
    if (err) return err;
  }
  if (p.alt_phone?.trim() && normPhone(p.alt_phone.trim()) === normPhone(p.phone.trim())) return "Alternate contact number must be different from the main number";
  return null;
};

export const FormSection = ({ step, title, children, testId }) => (
  <section className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4 lg:gap-10 py-7 border-t border-[#E5E7EB] first:border-t-0 first:pt-0" data-testid={testId}>
    <div>
      <div className="font-mono text-xs text-[#E60000] font-bold">{step}</div>
      <h3 className="font-heading text-lg font-extrabold tracking-tight">{title}</h3>
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

export const stepNo = (i) => String(i + 1).padStart(2, "0");

const ID_KEYS = ["pin", "gamls_id", "staff_id"];

const IdRow = ({ fields, p, set, slots }) => (
  <div className="col-span-full rounded-lg border-2 border-amber-400 bg-amber-50 p-4" data-testid="signup-ids-row">
    <div className="flex gap-2 text-sm text-amber-950 mb-3" role="note" data-testid="signup-ids-warning">
      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
      <p><strong>Very important:</strong> your AHPC PIN, GAMLS ID and Staff ID must be correct. Failure to provide correct information for these fields will result in you not receiving CPD points for the programme. The “I don’t have…” options must only be used by non-members, students and guests.</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
      {fields.map((f) => <FieldInput key={f.key} f={f} p={p} set={set} slots={slots} />)}
    </div>
  </div>
);

const SectionFields = ({ fields, p, set, slots, groupIds }) => {
  const ids = groupIds ? fields.filter((f) => ID_KEYS.includes(f.key)) : [];
  return fields.map((f) => {
    if (!ids.includes(f)) { return <FieldInput key={f.key} f={f} p={p} set={set} slots={slots} />; }
    return f === ids[0] ? <IdRow key="ids" fields={ids} p={p} set={set} slots={slots} /> : null;
  });
};

export const ProfileFields = ({ form, p, set, slots, groupIds = false }) => (
  <div data-testid="profile-fields">
    {form.sections.map((s, i) => (
      <FormSection key={s.id} step={stepNo(i)} title={s.title} testId={`profile-section-${s.id}`}>
        {s.fields.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
            <SectionFields fields={s.fields} p={p} set={set} slots={slots} groupIds={groupIds} />
          </div>
        ) : <p className="text-sm text-gray-400">No fields in this section yet.</p>}
      </FormSection>
    ))}
  </div>
);
